import { useCallback, useState } from "react";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import type { RuntimeWallet } from "../types/payments-wallets.types";

export interface UseWalletStatusActionsResult {
  isSubmitting: boolean;
  /** Freezes a wallet. Throws on failure so the caller can surface the backend message. */
  freeze: (id: string, reason: string) => Promise<RuntimeWallet>;
  /** Unfreezes a wallet. Throws on failure so the caller can surface the backend message. */
  unfreeze: (id: string) => Promise<RuntimeWallet>;
}

/**
 * Freeze/unfreeze actions for a payment wallet — INTERNAL staff only (`payments:write`; the backend also
 * enforces `userTypes: [INTERNAL]`, returning 403 otherwise, which callers surface via toast).
 */
export const useWalletStatusActions = (): UseWalletStatusActionsResult => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const freeze = useCallback(async (id: string, reason: string): Promise<RuntimeWallet> => {
    setIsSubmitting(true);
    try {
      const res = await PaymentsWalletsService.freeze(id, reason);
      return res.data;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const unfreeze = useCallback(async (id: string): Promise<RuntimeWallet> => {
    setIsSubmitting(true);
    try {
      const res = await PaymentsWalletsService.unfreeze(id);
      return res.data;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { isSubmitting, freeze, unfreeze };
};
